import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { LIST_REPORT_RESPONSES } from '@/api/graphql/queries/reportQueries';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface Employee {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

interface Question {
  id: string;
  header: string;
  format: string;
  questions: any;
  responseSchema: any;
}

interface Answer {
  id: string;
  questionId: string;
  answerText: any;
  extractedData?: any;
  question: Question;
}

interface Response {
  id: string;
  submittedAt: string;
  employee: Employee;
  answers: Answer[];
}

interface ReportResponsesResult {
  loading: boolean;
  error: Error | null;
  responses: Response[];
  hasMore: boolean;
  loadMore: () => void;
}

export function useReportResponses(
  reportId: string,
  enabled: boolean = true,
): ReportResponsesResult {
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Check authentication state
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    
    checkAuth();
    
    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  const { loading, error, data, fetchMore } = useQuery(LIST_REPORT_RESPONSES, {
    variables: {
      reportId,
      first: 20
    },
    skip: !enabled || !isAuthenticated,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onError: (err: Error) => {
      console.error('Error fetching report responses:', err);
      if (err.message.includes('401') || err.message.includes('unauthorized')) {
        toast.error('Session expired. Please refresh the page.');
      } else {
        toast.error('Failed to load responses');
      }
    }
  });

  // Process responses from data
  const responses = data?.listResponses?.edges?.map((edge: { node: Response }) => edge.node) || [];
  const pageInfo = data?.listResponses?.pageInfo;

  // Update pagination state when data changes
  useEffect(() => {
    if (pageInfo) {
      setHasMore(pageInfo.hasNextPage);
      setEndCursor(pageInfo.endCursor);
    }
  }, [pageInfo]);
  
  // Load more function
  const loadMore = () => {
    if (!enabled || !hasMore || loading || !reportId || !isAuthenticated) return;
    
    fetchMore({
      variables: {
        reportId,
        first: 20,
        after: endCursor
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        
        // Deduplicate edges based on node ID
        const existingIds = new Set(prev.listResponses.edges.map((edge: { node: { id: string } }) => edge.node.id));
        const newEdges = fetchMoreResult.listResponses.edges.filter(
          (edge: { node: { id: string } }) => !existingIds.has(edge.node.id)
        );
        
        return {
          listResponses: {
            ...fetchMoreResult.listResponses,
            edges: [...prev.listResponses.edges, ...newEdges]
          }
        };
      }
    });
  };
  
  if (!enabled || !isAuthenticated) {
    return {
      loading: false,
      error: null,
      responses: [],
      hasMore: false,
      loadMore: () => {},
    };
  }

  return {
    loading,
    error: error || null,
    responses,
    hasMore,
    loadMore
  };
} 